import * as React from "react";
import { Check, ChevronsUpDown, LoaderCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, } from "../../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger, } from "../../components/ui/popover";
/**
 * @example
 *
 * ```jsx
 * import { Combobox } from "@skve/components"
 *
 * const frameworks = [
 *   {
 *     value: "next.js",
 *     label: "Next.js",
 *   },
 *   {
 *     value: "sveltekit",
 *     label: "SvelteKit",
 *   },
 *   {
 *     value: "nuxt.js",
 *     label: "Nuxt.js",
 *   },
 *   {
 *     value: "remix",
 *     label: "Remix",
 *   },
 *   {
 *     value: "astro",
 *     label: "Astro",
 *   },
 * ]
 *
 * function Demo() {
 *   return (
 *     <Combobox
 *       items={frameworks}
 *       defaultValue="remix"
 *       search="Framework suchen"
 *     />
 *   )
 * }
 * ```
 */
export const Combobox = ({ defaultValue, value: customValue, className, onChange, items, empty, placeholder = React.createElement("div", null), search, searchValue, onSearchChange, onReachEnd, loading = false, }) => {
    var _a;
    const [open, setOpen] = React.useState(false);
    const controlled = React.useRef(customValue !== undefined);
    const [value, setValue] = React.useState(customValue !== null && customValue !== void 0 ? customValue : defaultValue);
    React.useEffect(() => {
        if (customValue !== value) {
            setValue(customValue);
            setOpen(false);
        }
    }, [controlled.current && customValue]);
    return (React.createElement(Popover, { open: open, onOpenChange: setOpen, modal: true },
        React.createElement(PopoverTrigger, { asChild: true },
            React.createElement(Button, { variant: "outline", role: "combobox", "aria-expanded": open, className: cn("w-full justify-between border-input bg-background hover:bg-background", className) },
                value
                    ? (_a = items.find((item) => item.value === value)) === null || _a === void 0 ? void 0 : _a.label
                    : placeholder,
                React.createElement(ChevronsUpDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" }))),
        React.createElement(PopoverContent, { className: "w-[--radix-popover-trigger-width] p-0" },
            React.createElement(Command, { shouldFilter: !onSearchChange },
                React.createElement(CommandInput, { placeholder: search, value: searchValue, onValueChange: onSearchChange }),
                React.createElement(CommandList, { onScroll: (event) => {
                        const list = event.currentTarget;
                        if (onReachEnd &&
                            !loading &&
                            list.scrollHeight - list.scrollTop - list.clientHeight < 40) {
                            onReachEnd();
                        }
                    } },
                    React.createElement(CommandEmpty, null, empty),
                    React.createElement(CommandGroup, null, items.map((item) => (React.createElement(CommandItem, { key: item.value, value: item.value, keywords: item.keywords, onSelect: (currentValue) => {
                            const nextValue = currentValue === value ? "" : currentValue;
                            if (!controlled.current) {
                                setValue(nextValue);
                                setOpen(false);
                            }
                            onChange === null || onChange === void 0 ? void 0 : onChange(nextValue);
                        } },
                        React.createElement(Check, { className: cn("mr-2 h-4 w-4", value === item.value ? "opacity-100" : "opacity-0") }),
                        item.label)))),
                    loading && (React.createElement("div", { className: "flex justify-center py-2", role: "status" },
                        React.createElement(LoaderCircle, { className: "h-4 w-4 animate-spin" }),
                        React.createElement("span", { className: "sr-only" }, "Weitere Eintr\u00E4ge werden geladen."))))))));
};
//# sourceMappingURL=combobox.js.map